// 권위: dashboard/api/src/.../pendingca/dto/PendingCADtos.java
// Spring Boot 의 Jackson 직렬화는 record 필드명을 그대로 camelCase 로 노출한다.
// envelope 언랩은 apiClient interceptor 가 처리 (data 만 도달).

export type ReviewStatus =
  | 'pending_review'
  | 'auto_accepted'
  | 'approved'
  | 'rejected'
  | 'edited';

export type EventType =
  | 'split'
  | 'reverse_split'
  | 'bonus_issue'
  | 'stock_dividend'
  | 'rights_offering'
  | 'cash_dividend'
  | string;

export interface PendingCAItem {
  auditId: number;
  corporateActionId: number | null;
  rcptNo: string | null;
  corpCode: string | null;
  symbol: string | null;
  disclosureTitle: string | null;
  disclosureUrl: string | null;
  llmModel: string | null;
  llmConfidence: number | null;
  ruleVerifiedFactor: number | null;
  reviewStatus: ReviewStatus;
  reviewer: string | null;
  reviewedAt: string | null;
  resyncStatus: string | null;
  createdAt: string;
  eventType: EventType | null;
  exDate: string | null;
  adjustmentFactor: number | null;
}

export interface PendingCASummary {
  pendingCount: number;
  autoAcceptedCount: number;
  todayNewCount: number;
}

export interface PendingCAListResponse {
  items: PendingCAItem[];
  total: number;
  summary: PendingCASummary;
}

export interface CorporateActionRow {
  id: number;
  symbol: string | null;
  corpCode: string | null;
  eventType: string | null;
  exDate: string | null;
  ratioOld: number | null;
  ratioNew: number | null;
  dividendRatio: number | null;
  adjustmentFactor: number | null;
  rcptNo: string | null;
  llmConfidence: number | null;
  appliedAt: string | null;
  sanityCheckResult: string | null;
  createdAt: string;
}

export interface PendingCADetail {
  auditId: number;
  corporateActionId: number | null;
  rcptNo: string | null;
  corpCode: string | null;
  symbol: string | null;
  disclosureTitle: string | null;
  disclosureUrl: string | null;
  disclosureBody: string | null;
  llmModel: string | null;
  llmPromptHash: string | null;
  llmResponseRaw: Record<string, unknown> | null;
  llmConfidence: number | null;
  llmCostUsd: number | null;
  extractedEvent: Record<string, unknown> | null;
  ruleVerifiedFactor: number | null;
  reviewStatus: ReviewStatus;
  reviewer: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  resyncStatus: string | null;
  resyncError: string | null;
  createdAt: string;
  corporateAction: CorporateActionRow | null;
}

export interface ActionResponse {
  auditId: number;
  reviewStatus: ReviewStatus;
  corporateActionId: number | null;
  resyncStatus: string | null;
  reviewedAt: string;
}

export interface BulkApproveError {
  auditId: number;
  code: string;
  message: string;
}

export interface BulkApproveResponse {
  requested: number;
  succeeded: number;
  failed: number;
  errors: BulkApproveError[];
}

export interface BulkApproveFilter {
  confidenceMin?: number;
  reviewStatus?: string;
  eventType?: string;
  from?: string;
  to?: string;
}

export interface BulkApproveRequest {
  auditIds?: number[];
  filter?: BulkApproveFilter;
}

// 목록 필터 (URL query params 으로 평탄화)
export interface PendingCAFilters {
  reviewStatus: ReviewStatus[];   // CSV 로 직렬화
  symbol: string;                 // 종목코드 또는 corp_name 부분 일치
  eventType: EventType | '';
  confidenceMin: number;          // 0.00 ~ 1.00
  from: string;                   // ISO8601
  to: string;                     // ISO8601
  page: number;
  size: number;
}

export const DEFAULT_FILTERS: PendingCAFilters = {
  // 페이지 진입 시 미검수(pending_review) 건만 노출 — 운영자는 신규 검수 대기열이 최우선.
  // 사용자는 필터 바에서 다른 상태를 자유롭게 토글해 확장할 수 있다.
  reviewStatus: ['pending_review'],
  symbol: '',
  eventType: '',
  confidenceMin: 0,
  from: '',
  to: '',
  page: 0,
  size: 50,
};

export const ALL_REVIEW_STATUSES: ReviewStatus[] = [
  'pending_review',
  'auto_accepted',
  'approved',
  'rejected',
  'edited',
];

export const ALL_EVENT_TYPES: EventType[] = [
  'split',
  'reverse_split',
  'bonus_issue',
  'stock_dividend',
  'rights_offering',
  'cash_dividend',
];

// WebSocket /topic/pending-ca 페이로드 (EventStompBridge.compactPendingPayload)
export interface PendingCAPushEvent {
  auditId: number | null;
  symbol: string | null;
  eventType: string | null;
  confidence: number | null;
  createdAt: string | null;
}
